/**
 * The auth surface's glyphs, traced path-for-path from the approved auth mockup.
 *
 * They are inline SVG rather than a sprite or a font because the mockup draws them that way and
 * because `currentColor` is what makes one glyph work in both themes. `docs/design/README.md`
 * records that the shipping icon set is `Icon.glyphs` ported from mobile — these are the mockup's
 * own placeholders, and they are what the approved specimen shows.
 *
 * Every glyph is `aria-hidden`: each one sits next to a labelled control or a message that already
 * says what it means, so announcing it twice would be noise. `EyeOffIcon` is the one addition — the
 * mockup only draws the "show" state, and a toggle whose two states look identical is not a toggle.
 * It is the same eye with a stroke through it.
 */

interface IconProps {
  className?: string;
}

const STROKE_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

export function MailIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <path d="M3 6.5l9 6 9-6" />
    </svg>
  );
}

/**
 * The mobile app. Not in the mockup — the screen it leads is not the mockup's refusal screen, which
 * used the warning plate. This is a redirection, not a failure: nothing went wrong, the visitor is
 * simply somewhere their account does not live, and a warning triangle would say otherwise.
 */
export function PhoneIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
    </svg>
  );
}

export function LockIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function EyeIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" />
      <circle cx="12" cy="12" r="2.6" />
    </svg>
  );
}

export function EyeOffIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M4 4l16 16" />
    </svg>
  );
}

export function WarningIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M12 3.8L2.6 20h18.8L12 3.8z" />
      <path d="M12 10v4" />
      <path d="M12 17.2v.1" />
    </svg>
  );
}

/** The date-of-birth field's leading glyph — the mockup's calendar, screen 6. */
export function CalendarIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </svg>
  );
}

/**
 * A met password criterion, and the "passwords match" message. The mockup draws both as a `✓`
 * character; a glyph is used instead so the mark scales with the type and inherits `currentColor`
 * rather than depending on whichever font happens to carry that codepoint.
 */
export function CheckIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M4.5 12.5l5 5 10-11" />
    </svg>
  );
}

/**
 * An unmet password criterion. The mockup only draws the all-met state, so this is the mobile
 * app's own unmet mark (`PasswordChecklist` renders an `x`) rather than an invention — the two
 * clients show the same checklist for the same rule.
 */
export function CrossIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** The role card's trailing chevron — the mockup's `›`, screen 3. */
export function ChevronRightIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE_PROPS}>
      <path d="M9.5 5l7 7-7 7" />
    </svg>
  );
}
